import { withBxApiContext } from 'helpers/withBxApiContext';
import bxApi from 'services/bxApi';

import { ROUTE_TYPES } from 'app/(clickouts)/constants';
import {
  BuyerIntentProfileView,
  getBuyerIntentCategoryId,
  getBuyerIntentProductId,
  warnMissingBuyerIntentIds,
} from 'components/BuyerIntent';
import {
  transformToBackendCustomerSupport,
  transformToBackendKeyFeatures,
  transformToBackendProsCons,
} from 'data/ai-content-rich';
import transformListingDetailProps from 'helpers/dataTransformers/listingDetail';
import {
  getBxApiProfile,
  transformListingDetailCharts,
} from 'helpers/dataTransformers/listingDetailBxApi';
import { generateListingDetailFaq } from 'helpers/dataTransformers/listingDetailFaq';
import { buildListingDetailPageData } from 'helpers/dataTransformers/listingDetailPageData';
import { getGeoipDetails } from 'helpers/geoipDetector';
import { getAppData, setCacheTags } from 'helpers/initializePage';
import hasALiveChatCategory, { hasPplCategory } from 'helpers/liveChat';
import { ROUTE_NAMES } from 'routes';
import ListingDetail from 'screens/listingDetail';
import { getPplCategories } from 'services/pplCategories/pplCategories';
import publishedPagesService from 'services/publishedPages';
import richContentService, {
  listingDetailTransformer,
} from 'services/richContent';

const loadAiContentRichJson = async (importFn) => {
  try {
    return (await importFn()).default;
  } catch {
    return null;
  }
};

const getSingleAiContentRichEntry = (data) =>
  Array.isArray(data) ? data[0] : data;

const normalizeFeatureTopicKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const getFeatureSnippetTypeFromSentiment = (sentiment) =>
  String(sentiment || '').toLowerCase() === 'negative' ? 'cons' : 'pros';

const transformFeatureReviewSnippet = (snippetNode) => {
  if (!snippetNode?.text) {
    return null;
  }

  const reviewer = snippetNode.textReview?.reviewer;
  const userName = [reviewer?.firstName, reviewer?.lastName]
    .filter(Boolean)
    .join(' ');
  const isValidated = reviewer?.isValidated || false;

  return {
    id:
      snippetNode.textReview?.id ||
      `${snippetNode.text}-${snippetNode.sentiment || 'neutral'}`,
    text: snippetNode.text,
    type: getFeatureSnippetTypeFromSentiment(snippetNode.sentiment),
    first_name: reviewer?.firstName || '',
    last_name: reviewer?.lastName || '',
    user_name: userName,
    job_title: reviewer?.jobTitle || '',
    avatar: reviewer?.profilePictureUrl || null,
    is_validated: isValidated,
    is_anonymous: !userName,
    is_authenticated: isValidated,
    created: snippetNode.textReview?.writtenOn || '',
    frequency_of_use: '',
    time_used: '',
    validations_passed: isValidated ? ['LinkedIn'] : [],
  };
};

const dedupeFeatureSnippets = (snippets) => {
  const seen = new Set();

  return snippets.filter((snippet) => {
    const key = snippet?.id || snippet?.text;

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const buildFeatureSnippetLookup = (featureSnippetNodesByTopic = {}) =>
  new Map(
    Object.entries(featureSnippetNodesByTopic).map(([topic, snippetNodes]) => [
      normalizeFeatureTopicKey(topic),
      (snippetNodes || []).map(transformFeatureReviewSnippet).filter(Boolean),
    ])
  );

const mergeAiKeyFeatures = (
  listingFeatures = {},
  transformedKeyFeatures,
  featureSnippetNodesByTopic = {}
) => {
  if (!transformedKeyFeatures) {
    return listingFeatures;
  }

  const featureSnippetLookup = buildFeatureSnippetLookup(
    featureSnippetNodesByTopic
  );

  const featureLookup = new Map(
    (listingFeatures.list || []).flatMap((feature) => {
      const keys = [
        feature.global_feature_id,
        normalizeFeatureTopicKey(feature.name),
      ].filter(Boolean);

      return keys.map((key) => [key, feature]);
    })
  );

  const enhancedFeatures = transformedKeyFeatures.enhanced_features.map(
    (feature) => {
      const matchedFeature =
        featureLookup.get(feature.global_feature_id) ||
        featureLookup.get(normalizeFeatureTopicKey(feature.name)) ||
        {};
      const relatedReviewSnippets =
        featureSnippetLookup.get(normalizeFeatureTopicKey(feature.name)) || [];
      const mergedSnippets = dedupeFeatureSnippets([
        ...(matchedFeature.snippets || []),
        ...(feature.snippets || []),
        ...relatedReviewSnippets,
      ]).slice(0, 4);

      return {
        ...matchedFeature,
        ...feature,
        definition: matchedFeature.definition || '',
        avg_rating: feature.avg_rating || matchedFeature.avg_rating || 0,
        cnt_rating: matchedFeature.cnt_rating || feature.cnt_rating || 0,
        snippets: mergedSnippets,
      };
    }
  );

  const enhancedFeatureNames = new Set(
    enhancedFeatures.map((feature) => feature.name)
  );
  const remainingFeaturesList = (listingFeatures.list || []).filter(
    (feature) => !enhancedFeatureNames.has(feature.name)
  );

  return {
    ...listingFeatures,
    ...transformedKeyFeatures,
    list: remainingFeaturesList,
    enhanced_features: enhancedFeatures,
    total: listingFeatures.total || transformedKeyFeatures.total,
    popular: listingFeatures.popular || [],
  };
};

export const getServerSideProps = withBxApiContext(
  'listing_detail',
  async (ctx) => {
    const isCypressTakingSnapshots =
      ctx.req.headers['fe-cypress-taking-snapshots'] === '0';
    // QA-only variant override for previews. Accepts ?ppl_variant=0 (control)
    // or ?ppl_variant=1 (variant). Non-production only; ignored otherwise.
    // Only bare '0' or '1' strings are honored — anything else (arrays,
    // empty, NaN-coerceable) falls through to the real flag+eligibility gate.
    const pplVariantParam = ctx.query.ppl_variant;
    const forcedPplVariant =
      process.env.TARGET_ENV !== 'production' &&
      (pplVariantParam === '0' || pplVariantParam === '1')
        ? Number(pplVariantParam)
        : null;
    const { slug } = ctx.query || {};
    const { countryCode, provider: geoipProvider } = getGeoipDetails(ctx);
    const appHost = `https://${ctx.req.headers['x-forwarded-host'] || ctx.req.headers.host}`;

    let results;
    try {
      results = await Promise.all([
        bxApi.getSpotlightHreflangsBySlug(ctx.query.slug).catch(() => []),
        richContentService.listingDetail(ctx).catch(() => ({
          richContent: null,
          bxApiData: null,
          resourcesData: null,
        })),
        publishedPagesService.getPublishedPage(slug).catch(() => ({})),
        bxApi.getListingDetailProductBySlug({
          slug: isCypressTakingSnapshots ? 'mailchimp' : slug,
          countryCode,
          categorySelection: ['HIGHEST_CPC_DIRECTORY', 'PRIMARY'],
          relatedProductsCategorySelection: [
            'HIGHEST_CPC_DIRECTORY',
            'PRIMARY',
          ],
        }),
        getPplCategories({ countryCode }).catch(() => []),
      ]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[listingDetail] Promise.all error:',
        error?.message,
        error?.response
      );
      ctx.res.statusCode = error?.statusCode || error?.status || 500;

      return {
        props: {
          appData: getAppData(ctx, {}),
          error: {
            statusCode: error?.statusCode || error?.status || 500,
          },
        },
      };
    }

    const [
      hreflangs,
      listingDetailRichContent,
      publishedPages,
      bxApiProduct,
      pplCategories,
    ] = results;

    if (!bxApiProduct) {
      return { notFound: true };
    }

    const requestPath = ctx.req.url.split('?')[0];
    const destinationPath = publishedPages?.destination
      ? new URL(publishedPages.destination, appHost).pathname
      : null;

    if (
      !isCypressTakingSnapshots &&
      destinationPath &&
      destinationPath !== requestPath
    ) {
      return {
        redirect: {
          destination: destinationPath,
          statusCode: 301,
        },
      };
    }

    if (
      !isCypressTakingSnapshots &&
      publishedPages?.[ROUTE_NAMES.LISTING_DETAILS] === false
    ) {
      if (destinationPath) {
        return {
          redirect: {
            destination: destinationPath,
            statusCode: 301,
          },
        };
      }

      return { notFound: true };
    }

    const { pageData, newNavigationTabs, categorySlug } =
      buildListingDetailPageData({
        ctx,
        appHost,
        product: bxApiProduct,
        publishedPages,
        countryCode,
        geoipProvider,
        pplCategories,
      });
    const appData = { ...getAppData(ctx, pageData), hreflangs };
    appData.route = ROUTE_TYPES.LISTING_DETAIL;
    appData.newNavigationTabs = newNavigationTabs;
    appData.head.data_layer[0].browserName = appData.userAgent?.browser || null;

    let mergedListing = getBxApiProfile(bxApiProduct, {});

    if (publishedPages) {
      mergedListing = publishedPagesService.patchListingWithBxApiPublishedPages(
        mergedListing,
        publishedPages
      );
    }

    mergedListing.g2ProductSlug =
      !mergedListing.is_premium && mergedListing.global_product_id
        ? await bxApi
            .getG2ProductSlug({
              id: mergedListing.global_product_id,
              countryCode,
            })
            .then((res) => res?.product?.slug ?? null)
            .catch(() => null)
        : null;

    if (!mergedListing.cta_urls?.cta) {
      mergedListing.cta_urls = {
        ...mergedListing.cta_urls,
        cta:
          mergedListing.cta_urls?.alternatives ||
          mergedListing.cta_urls?.detail ||
          null,
      };
    }

    const [aiProsConsData, aiCustomerSupportData, aiKeyFeaturesData] =
      await Promise.all([
        loadAiContentRichJson(
          () =>
            import(`data/ai-content-rich/pros-and-cons/${bxApiProduct.id}.json`)
        ),
        loadAiContentRichJson(
          () =>
            import(
              `data/ai-content-rich/customer-support/${bxApiProduct.id}.json`
            )
        ),
        loadAiContentRichJson(
          () =>
            import(`data/ai-content-rich/key-features/${bxApiProduct.id}.json`)
        ),
      ]);

    if (aiProsConsData) {
      mergedListing.prosCons = transformToBackendProsCons(
        getSingleAiContentRichEntry(aiProsConsData)
      );
    }

    if (aiCustomerSupportData) {
      mergedListing.customer_support_summary =
        transformToBackendCustomerSupport(
          getSingleAiContentRichEntry(aiCustomerSupportData)
        );
    }

    if (aiKeyFeaturesData) {
      const transformedKeyFeatures =
        transformToBackendKeyFeatures(aiKeyFeaturesData);

      if (transformedKeyFeatures) {
        const featureTopics = transformedKeyFeatures.enhanced_features
          .map((feature) => feature.name)
          .filter(Boolean);
        const bxFeatureSnippetsByTopic =
          await bxApi.getListingDetailFeatureSnippetsBySeoId({
            seoId: bxApiProduct.seoId,
            countryCode,
            topics: featureTopics,
            snippetCount: 4,
          });

        mergedListing.features = mergeAiKeyFeatures(
          mergedListing.features,
          transformedKeyFeatures,
          bxFeatureSnippetsByTopic
        );
      }
    }

    // Generate FAQ from listing data
    mergedListing.faq = generateListingDetailFaq(mergedListing);

    const useCaseCategoryIds = [
      ...new Set(
        (
          bxApiProduct.companyFitReviews?.facetSet?.selectedCategoryBuckets ||
          []
        )
          .filter((bucket) => bucket?.count > 0)
          .map((bucket) => bucket.value)
      ),
    ];
    const categoryNameMap = await bxApi.getSoftwareCategoryNamesByIds({
      categoryIds: useCaseCategoryIds,
      countryCode,
    });
    const charts = transformListingDetailCharts(bxApiProduct, categoryNameMap);

    // Add pricing plans if pricing page is not published
    const hasPricingPage = !!appData.header.tab_urls.find(
      (tab) => tab.route_name === ROUTE_NAMES.PRICING
    );

    // We pass in current Request this info to Document in order to avoid double calculation.
    ctx.req.APP_DATA = appData;
    setCacheTags(ctx, ROUTE_NAMES.LISTING_DETAILS);

    const [{ geoipCountryCode } = {}] = appData.head.data_layer || [];

    const hasPplCategoryValue = hasPplCategory(pplCategories, categorySlug);
    appData.head.hasPplCategory = hasPplCategoryValue;
    appData.head.hasLiveChatEnabled = hasALiveChatCategory(
      pplCategories,
      mergedListing?.categories
    );
    appData.head.hasPplDemoFormEnabled =
      ['US', 'CA'].includes(geoipCountryCode) && hasPplCategoryValue;
    appData.head.hasPplExitIntentEnabled =
      ['US', 'CA'].includes(geoipCountryCode) && hasPplCategoryValue;

    if (listingDetailRichContent?.richContent) {
      const metaDesc = `Considering ${mergedListing.name} for your software needs? Read our detailed review for insights on pricing, features, and user feedback to make an informed decision.`;
      appData.head.meta.og.og_desc = metaDesc;
      if (appData.head.meta.og?.extra_metas[0]) {
        appData.head.meta.og.extra_metas[0].content = metaDesc;
      }

      appData.head.data_layer.push({ content_type: 'Content Rich' });

      const title = `${mergedListing.name} Review ${listingDetailRichContent.richContent.titleYear}: Pricing, Features & Alternatives`;
      appData.head.title = title;
    }

    const contentRichSections = [];
    if (mergedListing.features?.heading)
      contentRichSections.push('key-features');
    if (
      mergedListing.prosCons?.pros?.length > 0 ||
      mergedListing.prosCons?.cons?.length > 0
    )
      contentRichSections.push('pros-and-cons');
    if (mergedListing.key_takeaways_summary)
      contentRichSections.push('key-takeaways');
    if (mergedListing.reviews_summary)
      contentRichSections.push('reviews-summary');
    if (mergedListing.customer_support_summary)
      contentRichSections.push('customer-support');
    if (mergedListing.value_for_money_summary)
      contentRichSections.push('value-for-money');
    if (contentRichSections.length > 0) {
      appData.head.data_layer[0].contentRichSection =
        contentRichSections.join(',');
      appData.head.data_layer[0].content_type = 'Content Rich AI';
    }

    return {
      props: transformListingDetailProps({
        appData: {
          ...appData,
          hasRichContent: !!listingDetailRichContent.richContent,
        },
        listing: mergedListing,
        listingDetailRichContent: listingDetailTransformer(
          listingDetailRichContent
        ),
        showPricingPlans: !hasPricingPage,
        showPplPricingBanner:
          forcedPplVariant != null
            ? forcedPplVariant === 1 && !mergedListing.is_premium
            : appData.pplPricingBannerVariant === 1 &&
              !!appData.header.ppl_category &&
              !!appData.header.ppl_category_country &&
              !mergedListing.is_premium,
        charts,
      }),
    };
  }
);

const ListingDetailPage = (props) => {
  const productId = getBuyerIntentProductId({
    appData: props.appData,
    listing: props.listing,
  });
  const categoryId = getBuyerIntentCategoryId(props.appData);

  if (!productId) {
    warnMissingBuyerIntentIds('page:listing-detail', {
      productId: props.listing?.global_product_id,
      categoryId,
    });

    return <ListingDetail {...props} />;
  }

  return (
    <BuyerIntentProfileView
      productId={productId}
      categoryId={categoryId}
      sourceLocation="page:listing-detail"
    >
      <ListingDetail {...props} />
    </BuyerIntentProfileView>
  );
};

ListingDetailPage.ddogName = ListingDetail.ddogName;
ListingDetailPage.Layout = ListingDetail.Layout;
ListingDetailPage.hasBuyerIntentPageView = true;

export default ListingDetailPage;
